import { useRef, useState } from "react";
import { Link } from "react-router";
import { Library } from "lucide-react";
import {
  formatBytes,
  useDeleteResume,
  useResumes,
  useUploadResume,
  type ResumeRow,
} from "../lib/resumes";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { EmptyState } from "../components/ui/EmptyState";
import {
  TableShell,
  Table as UITable,
  THead,
  TRow,
  TCell,
} from "../components/ui/Table";
import { Skeleton, SkeletonRow } from "../components/ui/Skeleton";

const LABEL_RE = /^[a-zA-Z0-9_-]{1,40}$/;

export default function Resumes() {
  const q = useResumes();
  const upload = useUploadResume();
  const del = useDeleteResume();

  const [label, setLabel] = useState("master");
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const labelOk = LABEL_RE.test(label);
  const fileOk = file && file.size > 0 && file.size <= 5 * 1024 * 1024;
  const canSubmit = labelOk && fileOk && !upload.isPending;

  function reset() {
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !file) return;
    upload.mutate({ label, file }, { onSuccess: () => reset() });
  }

  return (
    <section className="grid grid-cols-1 gap-6 p-4 sm:p-6 lg:grid-cols-[24rem_1fr] lg:p-8">
      <Card className="lg:col-span-2 p-4 border-accent-500/40 bg-accent-500/5">
        <div className="flex items-start gap-3">
          <Library size={16} className="mt-0.5 shrink-0 text-accent-300" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-neutral-200">
              <strong className="font-semibold text-neutral-50">
                These uploaded resumes seed your career corpus.
              </strong>{" "}
              Tailored resumes are now composed per-JD from the corpus —
              not rewritten from a base PDF. Import each resume into{" "}
              <Link
                className="text-accent-300 underline hover:text-accent-200"
                to="/corpus"
              >
                /corpus
              </Link>{" "}
              once, then use the resume tailor to compose tailored variants.
            </p>
          </div>
        </div>
      </Card>
      <Card className="self-start p-5 space-y-4">
        <div>
          <h2 className="text-xs uppercase tracking-wider text-neutral-500">
            Upload resume variant
          </h2>
          <p className="mt-2 text-xs text-neutral-500">
            PDF only. The plaintext is extracted and cached at upload time so
            the fit-summary panel can re-use it without re-parsing.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
              Label
            </span>
            <Input
              className="mt-1"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="master"
              invalid={!labelOk && label.length > 0}
              aria-describedby="resume-label-hint"
            />
            {!labelOk && label.length > 0 ? (
              <span
                id="resume-label-hint"
                className="mt-1 block text-[11px] text-danger-fg"
              >
                a-z A-Z 0-9 _ - (1-40 chars)
              </span>
            ) : null}
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
              PDF file
            </span>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              className="mt-1 block w-full text-sm text-neutral-300 file:mr-3 file:rounded-md file:border file:border-neutral-700 file:bg-neutral-900 file:px-3 file:py-1 file:text-xs file:text-neutral-200 hover:file:bg-neutral-800"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <span className="mt-1 block text-[11px] text-neutral-500">
                {file.name} · {formatBytes(file.size)}
              </span>
            ) : null}
          </label>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={!canSubmit}
            loading={upload.isPending}
          >
            Upload
          </Button>
        </form>
      </Card>

      <div className="min-w-0">
        <header className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Resumes</h1>
          <span className="text-xs text-neutral-500" aria-live="polite">
            {q.data ? (
              `${q.data.resumes.length} variants`
            ) : q.isLoading ? (
              <Skeleton className="inline-block h-3 w-20" />
            ) : null}
          </span>
        </header>
        <p className="mt-2 text-sm text-neutral-400">
          Multiple variants per user. Used by the AI fit summary and cover-letter generation.
        </p>

        <div className="mt-4">
          {q.isLoading ? (
            <TableShell>
              <UITable>
                <THead>
                  <HeaderRow />
                </THead>
                <tbody>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <SkeletonRow key={i} cols={4} />
                  ))}
                </tbody>
              </UITable>
            </TableShell>
          ) : q.error ? (
            <TableShell>
              <EmptyState
                tone="danger"
                title="Couldn't load resumes"
                description={String(q.error)}
                action={
                  <Button variant="secondary" onClick={() => q.refetch()}>
                    Retry
                  </Button>
                }
              />
            </TableShell>
          ) : q.data && q.data.resumes.length === 0 ? (
            <TableShell>
              <EmptyState title="No resume variants uploaded yet. Drop a PDF on the left to start." />
            </TableShell>
          ) : (
            <TableShell>
              <UITable>
                <THead>
                  <HeaderRow />
                </THead>
                <tbody>
                  {q.data?.resumes.map((row) => (
                    <ResumeRowView
                      key={row.id}
                      row={row}
                      pending={del.isPending && del.variables === row.id}
                      onDelete={() => {
                        if (confirm(`Delete resume "${row.label}"?`)) {
                          del.mutate(row.id);
                        }
                      }}
                    />
                  ))}
                </tbody>
              </UITable>
            </TableShell>
          )}
        </div>
      </div>
    </section>
  );
}

function HeaderRow() {
  return (
    <tr>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Label
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Size
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Uploaded
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium" />
    </tr>
  );
}

function ResumeRowView({
  row,
  pending,
  onDelete,
}: {
  row: ResumeRow;
  pending: boolean;
  onDelete: () => void;
}) {
  return (
    <TRow className="text-neutral-200">
      <TCell className="font-medium">{row.label}</TCell>
      <TCell className="text-neutral-400">{formatBytes(row.bytes)}</TCell>
      <TCell className="text-neutral-400">
        {new Date(row.uploaded_at).toLocaleString()}
      </TCell>
      <TCell className="text-right">
        <Button
          variant="secondary"
          size="sm"
          loading={pending}
          onClick={onDelete}
        >
          Delete
        </Button>
      </TCell>
    </TRow>
  );
}
